import type { CameraBookmark, Connection, SpatialObject, WorkspaceDocument, WorkspaceId } from '@/types';
import { asApplicationId, asBookmarkId, asConnectionId } from '@/types';
import { createEmptyDocument } from '@/store/workspaceStore';
import {
  createBookmark,
  createFile,
  createFolder,
  createGroup,
  createNote,
  createWindow,
} from '@/features/objects/objectFactory';
import { createId } from '@/utils/id';

/**
 * A real workspace built from the same factories and types the editor uses: a plan
 * board with nested groups, typed connections, files, a bookmark, an open Notes
 * window with content, and two camera bookmarks. Nothing here is special-cased by
 * the renderer — every object is ordinary document state.
 */
export const buildShowcaseWorkspace = (id: WorkspaceId, now = Date.now()): WorkspaceDocument => {
  const doc = createEmptyDocument(id, 'Showcase · Launch plan', now);
  const ws = { workspaceId: id };
  let z = 1;
  const next = () => z++;

  const guide = createNote({
    ...ws,
    position: { x: 40, y: 40 },
    zIndex: next(),
    size: { width: 300, height: 220 },
    name: 'Read me',
    color: 'sand',
    content:
      'Drag the field to pan, wheel to zoom.\nClick to select, Shift-click or marquee to multi-select.\nAlt-drag between objects to connect them.\n⇧⌘P opens the command palette, ⌘F searches, ⌘K launches apps.\n⇧⌘D shows engine diagnostics.',
  });

  // Research group with a nested "sources" folder.
  const research = createGroup({
    ...ws,
    position: { x: 420, y: 40 },
    zIndex: next(),
    size: { width: 560, height: 380 },
    name: 'Research',
  });
  const interviews = createNote({
    ...ws,
    position: { x: 20, y: 44 },
    zIndex: next(),
    name: 'Interviews',
    color: 'moss',
    content: '12 sessions done.\nTop themes: onboarding, offline, export.',
  });
  const survey = createNote({
    ...ws,
    position: { x: 280, y: 44 },
    zIndex: next(),
    name: 'Survey',
    color: 'sky',
    content: 'n = 340\nNPS 41 (+6)',
  });
  const sources = createFolder({
    ...ws,
    position: { x: 20, y: 220 },
    zIndex: next(),
    size: { width: 520, height: 140 },
    name: 'Sources',
  });
  const report = createFile({
    ...ws,
    position: { x: 20, y: 44 },
    zIndex: next(),
    name: 'Research report',
    fileName: 'research-report.pdf',
    mimeType: 'application/pdf',
    byteSize: 1_842_003,
  });
  const dataset = createFile({
    ...ws,
    position: { x: 240, y: 44 },
    zIndex: next(),
    name: 'Survey data',
    fileName: 'survey-2026.csv',
    mimeType: 'text/csv',
    byteSize: 96_412,
  });

  // Build group: milestones that depend on each other.
  const build = createGroup({
    ...ws,
    position: { x: 420, y: 480 },
    zIndex: next(),
    size: { width: 560, height: 260 },
    name: 'Build',
  });
  const design = createNote({
    ...ws,
    position: { x: 20, y: 44 },
    zIndex: next(),
    name: 'Design system',
    content: 'Tokens, components, dark scheme.',
  });
  const engine = createNote({
    ...ws,
    position: { x: 280, y: 44 },
    zIndex: next(),
    name: 'Spatial engine',
    color: 'clay',
    content: 'Scene graph, quadtree, snapping, camera.',
  });

  // Launch group.
  const launch = createGroup({
    ...ws,
    position: { x: 1060, y: 480 },
    zIndex: next(),
    size: { width: 320, height: 260 },
    name: 'Launch',
  });
  const release = createNote({
    ...ws,
    position: { x: 20, y: 44 },
    zIndex: next(),
    name: 'Release 1.0',
    color: 'sand',
    content: 'Blocked until QA signs off.',
  });
  const qa = createNote({
    ...ws,
    position: { x: 1060, y: 300 },
    zIndex: next(),
    name: 'QA sign-off',
    content: 'E2E: 47 · unit: 190',
  });

  const site = createBookmark({
    ...ws,
    position: { x: 40, y: 320 },
    zIndex: next(),
    name: 'Design notes',
    url: 'https://m3.material.io',
    title: 'Material Design',
    description: 'Reference for elevation and motion.',
  });

  const notesWindow = createWindow({
    ...ws,
    position: { x: 40, y: 520 },
    zIndex: next(),
    size: { width: 340, height: 220 },
    name: 'Launch checklist',
    applicationId: asApplicationId('app.notes'),
  });
  const windowWithState: SpatialObject = {
    ...notesWindow,
    applicationState: { text: '☐ Freeze scope\n☐ QA sign-off\n☐ Write release notes\n☐ Tag 1.0' },
  };

  const withParent = (o: SpatialObject, parentId: SpatialObject['parentId']): SpatialObject => ({ ...o, parentId });
  const objects: SpatialObject[] = [
    guide,
    { ...research, childIds: [interviews.id, survey.id, sources.id] },
    withParent(interviews, research.id),
    withParent(survey, research.id),
    withParent({ ...sources, childIds: [report.id, dataset.id] }, research.id),
    withParent(report, sources.id),
    withParent(dataset, sources.id),
    { ...build, childIds: [design.id, engine.id] },
    withParent(design, build.id),
    withParent(engine, build.id),
    { ...launch, childIds: [release.id] },
    withParent(release, launch.id),
    qa,
    site,
    windowWithState,
  ];

  const edge = (
    sourceId: SpatialObject['id'],
    targetId: SpatialObject['id'],
    type: Connection['type'],
    label = '',
  ): Connection => ({
    id: asConnectionId(createId('con')),
    workspaceId: id,
    sourceId,
    targetId,
    type,
    label,
  });
  const connections: Connection[] = [
    edge(interviews.id, design.id, 'references', 'informs'),
    edge(survey.id, engine.id, 'references'),
    edge(engine.id, design.id, 'depends-on', 'needs tokens'),
    edge(release.id, engine.id, 'depends-on'),
    edge(qa.id, release.id, 'blocks', 'until green'),
    edge(site.id, design.id, 'related-to'),
    edge(windowWithState.id, release.id, 'linked-to', 'checklist'),
  ];

  const bookmarks: CameraBookmark[] = [
    {
      id: asBookmarkId(createId('bm')),
      name: 'Overview',
      camera: { position: { x: -40, y: -40 }, zoom: 0.8 },
      createdAt: now,
    },
    {
      id: asBookmarkId(createId('bm')),
      name: 'Build',
      camera: { position: { x: 380, y: 440 }, zoom: 1.2 },
      createdAt: now,
    },
  ];

  return {
    ...doc,
    workspace: { ...doc.workspace, bookmarks, camera: { position: { x: -40, y: -40 }, zoom: 0.8 } },
    objects: Object.fromEntries(objects.map((o) => [o.id, o])),
    connections: Object.fromEntries(connections.map((c) => [c.id, c])),
  };
};
